import { createHash } from "crypto-js";
import { authErrors, localStorageKeys } from "../const";
import { addToLocalStrage, deleteFromLocalStorage } from "../helper";
import { exchangeRefreshCode, fetchInitUserAuthInfo } from "../api/authApi";
import {
  emitAuthError,
  emitNeedAuth,
  emitSessionExpired,
  emitStoreAuthDetails,
} from "../util/eventEmitter";
import { lastPathsRef } from "../router/CustomBrowserRouter";

const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Error decoding token:", e);
    throw e;
  }
};

let refreshTokenPromise = null;

export const checkAuth = async () => {
  const expireTime = Number(localStorage.getItem(localStorageKeys.EXPIRE_TIME));
  const now = Math.floor(Date.now() / 1000);

  if (now < expireTime) {
    return;
  }

  if (refreshTokenPromise) {
    await refreshTokenPromise;
    return;
  }

  refreshTokenPromise = (async () => {
    try {
      const response = await exchangeRefreshCode();
      const data = await decodeAndStoreInLocalStorage(response);
      emitStoreAuthDetails(data);
    } catch (e) {
      const excluded = ["/login", "/unauthorized"];
      const expiredRoute =
        [...lastPathsRef.current].reverse().find((p) => !excluded.includes(p)) ||
        "/";
      localStorage.setItem(localStorageKeys.EXPIRED_ROUTE, expiredRoute);

      if (e?.response?.data?.error) {
        if (e.response.data.error === authErrors.INVALID_GRANT) {
          emitSessionExpired();
        } else {
          emitAuthError(e.response.data.error);
        }
      }
      // The session cannot be recovered. Drop the stale tokens so no further
      // request retries the refresh and so the store no longer reports the
      // user as authenticated.
      clearAuthDetails();
      emitNeedAuth();
      throw e;
    } finally {
      refreshTokenPromise = null;
    }
  })();
  await refreshTokenPromise;
};

// Generate a secure random string for the PKCE challenge
export const generateRandomString = (length) => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => ("0" + byte.toString(16)).slice(-2)).join(
    "",
  );
};

export const fetchCurrentCompany = (companies) => {
  if (!companies || companies.length === 0) {
    return -1;
  }

  const currentCompany = localStorage.getItem(
    localStorageKeys.USER.CURRENT_COMPANY,
  );

  if (
    currentCompany === null ||
    !companies.includes(parseInt(currentCompany))
  ) {
    localStorage.setItem(localStorageKeys.USER.CURRENT_COMPANY, companies[0]);
    return companies[0];
  }
  return currentCompany;
};

export const decodeAndStoreInLocalStorage = async (response) => {
  const accessToken = response[localStorageKeys.ACCESS_TOKEN];
  const refreshToken = response[localStorageKeys.REFRESH_TOKEN];
  const decodedToken = decodeJWT(accessToken);

  const expiresIn = response[localStorageKeys.EXPIRES_IN];
  const tokenType = response[localStorageKeys.TOKEN_TYPE];
  const username = decodedToken[localStorageKeys.USER.USERNAME];
  const authTime = decodedToken[localStorageKeys.AUTH_TIME];
  const expireTime = decodedToken[localStorageKeys.EXPIRE_TIME];

  const { permissions, companies, types, userDetails, currentCompany } =
    await fetchInitUserAuthInfo(`${tokenType} ${accessToken}`);
  let permissionsMap = permissions ? JSON.parse(permissions) : {};

  // Dynamically inject BANK_MASTER permissions since it's not in the auth database yet
  if (!permissionsMap["masterdata"]) {
    permissionsMap["masterdata"] = {};
  }
  permissionsMap["masterdata"]["VIEW"] = true;
  permissionsMap["masterdata"]["BANK_MASTER"] = {
    FORM: {
      CREATE: true,
      UPDATE: true,
      VIEW: true,
      DELETE: true,
    },
    bnk_code: { VIEW: true, CREATE: true, UPDATE: true },
    bnk_name: { VIEW: true, CREATE: true, UPDATE: true },
    bnk_description: { VIEW: true, CREATE: true, UPDATE: true },
    bnk_company: { VIEW: true, CREATE: true, UPDATE: true },
    bnk_status: { VIEW: true, CREATE: true, UPDATE: true },
    bnk_active: { VIEW: true, CREATE: true, UPDATE: true },
  };

  addToLocalStrage({
    [localStorageKeys.ACCESS_TOKEN]: accessToken,
    [localStorageKeys.REFRESH_TOKEN]: refreshToken,
    [localStorageKeys.AUTH_TIME]: authTime,
    [localStorageKeys.EXPIRE_TIME]: expireTime,
    [localStorageKeys.EXPIRES_IN]: expiresIn,
    [localStorageKeys.TOKEN_TYPE]: tokenType,
    [localStorageKeys.USER.USERNAME]: username,
    [localStorageKeys.USER.CURRENT_COMPANY]: currentCompany,
  });

  return {
    username,
    permissions: permissionsMap,
    company: companies,
    types,
    userDetails,
    companyIds: companies ? companies.map((com) => com.comId) : [],
    currentCompany: currentCompany,
  };
};

// Encode the string to base64URL
function base64URLEncode(str) {
  return str
    .toString(createHash.enc.Base64)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

// Generate the code verifier and code challenge
export function generatePKCECodes() {
  const codeVerifier = generateRandomString(128);
  const challenge = base64URLEncode(createHash.SHA256(codeVerifier));
  return { codeVerifier, challenge };
}

export const getFormByTab = (section, tab) => {
  if (section === null || tab === null) return null;
  const formObj =
    Object.values(section).find((formObj) => formObj.tab === tab) ?? null;
  return formObj ? formObj.form : null;
};

export const clearAuthDetails = (deleteExpiredRoute = false) => {
  if (deleteExpiredRoute) {
    deleteFromLocalStorage([
      localStorageKeys.ACCESS_TOKEN,
      localStorageKeys.REFRESH_TOKEN,
      localStorageKeys.EXPIRES_IN,
      localStorageKeys.EXPIRE_TIME,
      localStorageKeys.TOKEN_TYPE,
      localStorageKeys.AUTH_TIME,
      localStorageKeys.USER.USERNAME,
      localStorageKeys.USER.AUTHORITIES,
      localStorageKeys.PREFERENCE.REMEMBER_ME,
      localStorageKeys.USER.ROLES,
      localStorageKeys.EXPIRED_ROUTE,
    ]);
  } else {
    deleteFromLocalStorage([
      localStorageKeys.ACCESS_TOKEN,
      localStorageKeys.REFRESH_TOKEN,
      localStorageKeys.EXPIRES_IN,
      localStorageKeys.EXPIRE_TIME,
      localStorageKeys.TOKEN_TYPE,
      localStorageKeys.AUTH_TIME,
      localStorageKeys.USER.USERNAME,
      localStorageKeys.USER.AUTHORITIES,
      localStorageKeys.PREFERENCE.REMEMBER_ME,
      localStorageKeys.USER.ROLES,
    ]);
  }
};

export const getModuleAuthority = (auth) => {
  if (!auth) return { view: false, authorized: false };
  return { view: auth?.VIEW };
};

export const getFormAuthority = (auth, form, editMode) => {
  const authority = auth?.[form]?.FORM;
  const { view: modView } = getModuleAuthority(auth);

  if (!authority) return { view: false, authorized: false };

  const deletePermission = authority?.DELETE;
  const authorized = editMode ? authority.UPDATE : authority.CREATE;
  const view = authority?.VIEW;

  return {
    view: view && modView,
    authorized: authorized && modView,
    deletePermission,
  };
};

export const getFieldAuthorityKeys = (auth, form, field) => {
  const authority = auth?.[form]?.[field];
  if (!authority) return [];
  return Object.keys(authority).filter((key) => authority[key]);
};

export const getFieldAuthority = (
  auth,
  form,
  field,
  needDeletePermission,
  editMode,
) => {
  const authority = auth?.[form]?.[field];
  if (!authority) return { view: false, authorized: false };

  const {
    view: formView,
    authorized: formAuthorized,
    deletePermission: formDelete,
  } = getFormAuthority(auth, form, editMode);

  const view = authority?.VIEW;
  const authorized = editMode
    ? needDeletePermission
      ? authority?.UPDATE && formDelete
      : authority?.UPDATE
    : authority?.CREATE;

  return { view: formView && view, authorized: formAuthorized && authorized };
};
